import type { InferSelectModel } from "drizzle-orm";
import {
  observatory,
  person,
  site,
  tag,
  label,
  timeserie,
  organization,
  project,
  observatoryStat,
  siteStat,
} from "@/db/schema";

interface Application {
  id: string;
  title: Record<string, string>;
  shortTitle: Record<string, string>;
  lead: Record<string, string>;
  definition?: Record<string, string>;
  icon: string;
  url: string;
}

interface NavItem {
  id: string;
  title: Record<string, string>;
  url: string;
}

type ObservatoryModel = InferSelectModel<typeof observatory>;

interface Observatory extends ObservatoryModel {
  persons?: ObservatoryPerson[];
  tags?: ObservatoryTag[];
  labels?: ObservatoryLabel[];
  stat?: ObservatoryStat;
  timeseries?: Timeserie[];
  sites?: Site[];
}

type QuickAccessObservatory = Omit<
  ObservatoryModel,
  | "type"
  | "theme"
  | "corporateWebsiteUrl"
  | "picture"
  | "hasData"
  | "startYear"
  | "catalogUrl"
>;

interface ObservatoryPerson {
  observatoryId: string;
  personId: number;
  person: Person;
}

interface ObservatoryTag {
  observatoryId: string;
  tagId: number;
  tag: Tag;
}

interface ObservatoryLabel {
  observatoryId: string;
  labelId: number;
  label: Label;
}

type ObservatoryStat = InferSelectModel<typeof observatoryStat>;

type ProjectModel = InferSelectModel<typeof project>;

interface Project extends ProjectModel {
  observatory?: Observatory | null;
}

interface User {
  name: string;
  email: string;
}

enum ProjectTypeEnum {
  GALLERY = "gallery",
  CITIZEN = "citizen",
}

interface Site extends InferSelectModel<typeof site> {
  observatory?: Observatory;
  stat?: SiteStat;
}

type SiteStat = InferSelectModel<typeof siteStat>;

type Timeserie = InferSelectModel<typeof timeserie>;

interface Tag extends InferSelectModel<typeof tag> {
  observatories?: Observatory[] | null;
}

interface Person extends InferSelectModel<typeof person> {
  organization?: Organization | null;
}

type Organization = InferSelectModel<typeof organization>;

type FooterOrganization = Omit<Organization, "id"> & {
  id: number | string;
  size?: { width: number; height: number };
};

type Label = InferSelectModel<typeof label>;

interface ThemePalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

interface WeatherStation {
  ficheClimComplete: number;
  ficheClimReduite: number;
  COMMUNE: string;
  LAT_DG: number;
  LON_DG: number;
  NUM_POSTE: number;
  NOM_USUEL: string;
  NUM_DEP: number;
  ALTI: number;
}

interface Place {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  category: string;
  type: string;
  place_rank: number;
  importance: number;
  addresstype: string;
  name: string;
  display_name: string;
  boundingbox: { 0: number; 1: number; 2: number; 3: number };
}

enum ObjectTypeEnum {
  OBSERVATION = "observation",
  DEVICE = "device",
  SITE = "site",
  TIMESERIE = "timeserie",
  THEME = "theme",
  TAG = "tag",
  PICTURE = "picture",
}

enum ThemeTypeEnum {
  EARTH = "earth",
  WATER = "water",
  UNIVERSE = "universe",
  BIODIVERSITY = "biodiversity",
}

type ThemeLegend = {
  label: string;
  type: ThemeTypeEnum;
  count: number;
};

enum SiteTypeEnum {
  POINT = "point",
  STATION = "station",
  ZONE = "zone",
}

type SiteCounters = Record<string, number>;

type MapLayersVisibility = Record<string, boolean>;

export {
  type Label,
  type Observatory,
  type QuickAccessObservatory,
  type ObservatoryPerson,
  type ObservatoryLabel,
  type ObservatoryTag,
  type Person,
  type Site,
  type SiteStat,
  type Tag,
  type Timeserie,
  type ThemePalette,
  type ThemeLegend,
  type WeatherStation,
  type Application,
  type Organization,
  type FooterOrganization,
  type Place,
  type Project,
  type SiteCounters,
  type MapLayersVisibility,
  type User,
  type NavItem,
  ThemeTypeEnum,
  ObjectTypeEnum,
  SiteTypeEnum,
  ProjectTypeEnum,
};
